import React, { useState } from 'react';
import { Sidebar, MobileDrawer, BottomTabBar, ClientSection } from './components/Sidebar';
import { TopBar, ViewMode } from './components/TopBar';
import { KpiCard } from './components/KpiCard';
import { ShipmentsChart } from './components/ShipmentsChart';
import { StatusDonut } from './components/StatusDonut';
import { ShipmentsTable } from './components/ShipmentsTable';
import { Spedizioni } from './components/operator/Spedizioni';
import { Ticket } from './components/operator/Ticket';
import { Anomalie } from './components/operator/Anomalie';
import { Ritardi } from './components/operator/Ritardi';
import { Giacenze } from './components/operator/Giacenze';
import { CorrieriPerformance } from './components/operator/CorrieriPerformance';
import { Contratti } from './components/operator/Contratti';
import { VerificaFatture } from './components/operator/VerificaFatture';
import { Integrazioni } from './components/operator/Integrazioni';
import { Store } from './components/operator/Store';
import { NewShipmentPage } from './components/operator/NewShipmentPage';
import { Magazzini } from './components/operator/Magazzini';
import { Colli } from './components/operator/Colli';
import { Analytics, AnalyticsSection } from './components/operator/Analytics';
import { SettingsGeneral, SettingsTeam, SettingsBilling, SettingsNotifications } from './components/operator/Settings';
import { Plus } from 'lucide-react';
import { ClientDashboard } from './components/client/ClientDashboard';
import { ClientSpedizioni } from './components/client/ClientSpedizioni';
import { ClientReturns } from './components/client/ClientReturns';
import { ClientTickets } from './components/client/ClientTickets';
import { ClientReturn } from './components/client/types';
import { Notifications, NotificationTarget } from './components/Notifications';
import { Profile } from './components/Profile';
import { Login } from './components/Login';
type AuthUser = {
  email: string;
  role: ViewMode;
} | null;
export function App() {
  const [authUser, setAuthUser] = useState<AuthUser>(null);
  const [clientSection, setClientSection] = useState<ClientSection>('panoramica');
  const [clientShipmentSelectedId, setClientShipmentSelectedId] = useState<string | null>(null);
  const openClientShipment = (id: string) => {
    setClientShipmentSelectedId(id);
    setClientSection('spedizioni');
  };
  const [clientTicketSelectedId, setClientTicketSelectedId] = useState<string | null>(null);
  const openClientTicket = (id: string) => {
    setClientTicketSelectedId(id);
    setClientSection('ticket');
  };
  const [clientReturns, setClientReturns] = useState<ClientReturn[]>([]);
  const addClientReturn = (r: ClientReturn) => {
    setClientReturns(prev => [r, ...prev]);
    // Auto-navigate the user to the Resi tab so they see their submission listed
    setClientSection('resi');
  };
  const [contextId, setContextId] = useState('shipments');
  const [activeItem, setActiveItem] = useState('Panoramica');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [overviewPeriod, setOverviewPeriod] = useState<'settimana' | 'mese' | 'trimestre' | 'anno'>('mese');
  const [newShipmentOpen, setNewShipmentOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState<{
    contextId: string;
    activeItem: string | null;
  } | null>(null);
  const [spedizioniSelectedId, setSpedizioniSelectedId] = useState<string | null>(null);
  const [ticketSelectedId, setTicketSelectedId] = useState<string | null>(null);
  const [anomalieSelectedId, setAnomalieSelectedId] = useState<string | null>(null);
  const openShipmentDetail = (id: string) => {
    setContextId('shipments');
    setActiveItem('Tutte le spedizioni');
    setSpedizioniSelectedId(id);
    setNotificationsOpen(false);
  };
  const handleNotificationNavigate = (target: NotificationTarget) => {
    setContextId(target.context);
    setActiveItem(target.activeItem);
    setSpedizioniSelectedId(target.shipmentId ?? null);
    setAnomalieSelectedId(target.anomalyId ?? null);
    setNotificationsOpen(false);
  };
  const handleLogin = (role: ViewMode, email: string) => {
    setAuthUser({
      email,
      role
    });
    if (role === 'client') setClientSection('panoramica');else {
      setContextId('shipments');
      setActiveItem('Panoramica');
    }
    setNotificationsOpen(false);
    setMobileNavOpen(false);
  };
  const handleLogout = () => {
    setAuthUser(null);
    setNotificationsOpen(false);
    setMobileNavOpen(false);
  };
  if (!authUser) {
    return <Login onLogin={handleLogin} data-id="element-0" />;
  }
  const viewMode = authUser.role;
  const renderOperatorSection = () => {
    if (newShipmentOpen) {
      return <NewShipmentPage onClose={() => setNewShipmentOpen(false)} data-id="element-1" />;
    }
    if (contextId === 'shipments' && activeItem === 'Tutte le spedizioni') {
      return <Spedizioni selectedId={spedizioniSelectedId} onSelectedIdChange={setSpedizioniSelectedId} onNewShipment={() => setNewShipmentOpen(true)} data-id="element-2" />;
    }
    if (contextId === 'tickets') {
      const sectionMap: Record<string, 'tutti' | 'da-approvare' | 'categorie'> = {
        'Tutti i ticket': 'tutti',
        'Resi da approvare': 'da-approvare',
        Categorie: 'categorie'
      };
      const section = sectionMap[activeItem] || 'tutti';
      return <Ticket section={section} selectedId={ticketSelectedId} onSelectedIdChange={setTicketSelectedId} data-id="element-3" />;
    }
    if (contextId === 'shipments' && activeItem === 'Anomalie') {
      return <Anomalie selectedId={anomalieSelectedId} onSelectedIdChange={setAnomalieSelectedId} data-id="element-4" />;
    }
    if (contextId === 'shipments' && activeItem === 'Ritardi') {
      return <Ritardi onOpenShipment={openShipmentDetail} data-id="element-5" />;
    }
    if (contextId === 'shipments' && activeItem === 'Giacenze') {
      return <Giacenze data-id="element-6" />;
    }
    if (contextId === 'shipments' && activeItem === 'Magazzini') {
      return <Magazzini data-id="element-7" />;
    }
    if (contextId === 'shipments' && activeItem === 'Colli') {
      return <Colli data-id="element-8" />;
    }
    if (contextId === 'carriers' && activeItem === 'Performance') {
      return <CorrieriPerformance data-id="element-9" />;
    }
    if (contextId === 'carriers' && activeItem === 'Contratti') {
      return <Contratti data-id="element-10" />;
    }
    if (contextId === 'carriers' && activeItem === 'Verifica Fatture') {
      return <VerificaFatture data-id="element-11" />;
    }
    if (contextId === 'carriers' && activeItem === 'Integrazioni') {
      return <Integrazioni data-id="element-12" />;
    }
    if (contextId === 'analytics') {
      const analyticsMap: Record<string, AnalyticsSection> = {
        Overview: 'overview',
        Report: 'report'
      };
      const section = analyticsMap[activeItem] || 'overview';
      return <Analytics section={section} data-id="element-13" />;
    }
    if (contextId === 'stores') {
      const openTickets = () => {
        setContextId('tickets');
        setActiveItem('Tutti i ticket');
        setTicketSelectedId(null);
      };
      const openGiacenze = () => {
        setContextId('shipments');
        setActiveItem('Giacenze');
      };
      const openRitardi = () => {
        setContextId('shipments');
        setActiveItem('Ritardi');
      };
      const storeProps = {
        onOpenTickets: openTickets,
        onOpenGiacenze: openGiacenze,
        onOpenRitardi: openRitardi
      };
      if (activeItem === 'Referenti') return <Store section="referenti" {...storeProps} data-id="element-14" />;
      if (activeItem === 'Statistiche') return <Store section="statistiche" {...storeProps} data-id="element-15" />;
      return <Store section="list" {...storeProps} data-id="element-16" />;
    }
    if (contextId === 'account') {
      if (activeItem === 'Impostazioni') return <SettingsGeneral data-id="element-17" />;
      if (activeItem === 'Team') return <SettingsTeam data-id="element-18" />;
      if (activeItem === 'Fatturazione') return <SettingsBilling data-id="element-19" />;
      if (activeItem === 'Notifiche') return <SettingsNotifications data-id="element-20" />;
    }
    return <div className="space-y-8" data-id="element-21">
        <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-22">
          <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-23">
            Panoramica
          </h1>
          <button type="button" onClick={() => setNewShipmentOpen(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-24">
            <Plus className="h-4 w-4" data-id="element-25" />
            Nuova spedizione
          </button>
        </div>

        <div className="flex items-center justify-end" data-id="element-26">
          <div className="inline-flex items-center p-0.5 bg-[#181818] border border-[#262626] rounded-lg" data-id="element-27">
            {([{
            id: 'settimana',
            label: 'Settimana'
          }, {
            id: 'mese',
            label: 'Mese'
          }, {
            id: 'trimestre',
            label: 'Trimestre'
          }, {
            id: 'anno',
            label: 'Anno'
          }] as const).map(p => {
            const active = overviewPeriod === p.id;
            return <button key={p.id} type="button" onClick={() => setOverviewPeriod(p.id)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${active ? 'bg-[#0A0A0A] text-[#FAFAFA] border border-[#262626]' : 'text-[#9A9A9A] hover:text-[#FAFAFA] border border-transparent'}`} data-id="element-28">
                  {p.label}
                </button>;
          })}
          </div>
        </div>

        <section aria-label="Indicatori chiave" className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-29">
          {(() => {
          const OVERVIEW_KPIS: Record<typeof overviewPeriod, {
            totale: {
              value: string;
              delta: number;
            };
            transito: {
              value: string;
              delta: number;
            };
            consegnati: {
              value: string;
              delta: number;
            };
            resi: {
              value: string;
              delta: number;
            };
          }> = {
            settimana: {
              totale: {
                value: '184',
                delta: 5.2
              },
              transito: {
                value: '42',
                delta: 3.1
              },
              consegnati: {
                value: '128',
                delta: 6.4
              },
              resi: {
                value: '7',
                delta: -12.5
              }
            },
            mese: {
              totale: {
                value: '842',
                delta: 4.1
              },
              transito: {
                value: '186',
                delta: 2.4
              },
              consegnati: {
                value: '612',
                delta: 5.8
              },
              resi: {
                value: '34',
                delta: -8.2
              }
            },
            trimestre: {
              totale: {
                value: '2.418',
                delta: 3.6
              },
              transito: {
                value: '522',
                delta: 1.9
              },
              consegnati: {
                value: '1.760',
                delta: 4.7
              },
              resi: {
                value: '98',
                delta: -4.1
              }
            },
            anno: {
              totale: {
                value: '9.640',
                delta: 8.2
              },
              transito: {
                value: '1.984',
                delta: 3.3
              },
              consegnati: {
                value: '7.022',
                delta: 9.1
              },
              resi: {
                value: '402',
                delta: -2.7
              }
            }
          };
          const k = OVERVIEW_KPIS[overviewPeriod];
          return <>
                <KpiCard label="Totale" value={k.totale.value} delta={k.totale.delta} data-id="element-30" />
                <KpiCard label="In transito" value={k.transito.value} delta={k.transito.delta} data-id="element-31" />
                <KpiCard label="Consegnati" value={k.consegnati.value} delta={k.consegnati.delta} data-id="element-32" />
                <KpiCard label="Richieste di reso" value={k.resi.value} delta={k.resi.delta} positiveIsGood={false} data-id="element-33" />
              </>;
        })()}
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-34">
          <div className="lg:col-span-2" data-id="element-35">
            <ShipmentsChart onViewReport={() => {
            setContextId('analytics');
            setActiveItem('Report');
          }} data-id="element-36" />
          </div>
          <div data-id="element-37">
            <StatusDonut onViewDetail={() => {
            setContextId('shipments');
            setActiveItem('Tutte le spedizioni');
          }} data-id="element-38" />
          </div>
        </section>

        <section className="pb-6" data-id="element-39">
          <ShipmentsTable onShipmentClick={openShipmentDetail} onViewAll={() => {
          setContextId('shipments');
          setActiveItem('Tutte le spedizioni');
        }} data-id="element-40" />
        </section>
      </div>;
  };
  const isClientMobile = viewMode === 'client';
  return <div className="dark min-h-screen w-full bg-[#0A0A0A] text-[#FAFAFA] font-heading antialiased flex" data-id="element-41">
      <Sidebar viewMode={viewMode} clientSection={clientSection} onClientSectionChange={s => {
      setClientSection(s);
      setNotificationsOpen(false);
      setProfileOpen(false);
    }} contextId={contextId} activeItem={activeItem} onContextChange={c => {
      if (newShipmentOpen) {
        setPendingNav(p => ({
          contextId: c,
          activeItem: p?.activeItem ?? null
        }));
        return;
      }
      setContextId(c);
      setNotificationsOpen(false);
    }} onActiveItemChange={i => {
      if (newShipmentOpen) {
        setPendingNav(p => ({
          contextId: p?.contextId ?? contextId,
          activeItem: i
        }));
        return;
      }
      setActiveItem(i);
      setNotificationsOpen(false);
      setProfileOpen(false);
    }} data-id="element-42" />

      <MobileDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} viewMode={viewMode} clientSection={clientSection} onClientSectionChange={s => {
      setClientSection(s);
      setNotificationsOpen(false);
      setProfileOpen(false);
    }} contextId={contextId} activeItem={activeItem} onContextChange={c => {
      if (newShipmentOpen) {
        setPendingNav(p => ({
          contextId: c,
          activeItem: p?.activeItem ?? null
        }));
        setMobileNavOpen(false);
        return;
      }
      setContextId(c);
      setNotificationsOpen(false);
      setProfileOpen(false);
    }} onActiveItemChange={i => {
      if (newShipmentOpen) {
        setPendingNav(p => ({
          contextId: p?.contextId ?? contextId,
          activeItem: i
        }));
        setMobileNavOpen(false);
        return;
      }
      setActiveItem(i);
      setNotificationsOpen(false);
      setProfileOpen(false);
    }} data-id="element-43" />

      <div className="flex-1 min-w-0 flex flex-col" data-id="element-44">
        <TopBar viewMode={viewMode} onMenuClick={() => setMobileNavOpen(true)} onNotificationsClick={() => {
        setProfileOpen(false);
        setNotificationsOpen(true);
      }} onProfileClick={() => {
        setNotificationsOpen(false);
        setProfileOpen(true);
      }} onLogout={handleLogout} onAccountNavigate={label => {
        setProfileOpen(false);
        setNotificationsOpen(false);
        setContextId('account');
        setActiveItem(label);
      }} onSearchSelect={target => {
        setNotificationsOpen(false);
        setProfileOpen(false);
        // Customer-side navigation
        if (target.clientShipmentId) {
          openClientShipment(target.clientShipmentId);
          return;
        }
        if (target.clientTicketId) {
          openClientTicket(target.clientTicketId);
          return;
        }
        if (target.clientSection) {
          setClientTicketSelectedId(null);
          setClientShipmentSelectedId(null);
          setClientSection(target.clientSection);
          return;
        }
        // Admin-side detail navigation
        if (target.openShipmentId) {
          openShipmentDetail(target.openShipmentId);
          return;
        }
        if (target.openTicketId) {
          setContextId('tickets');
          setActiveItem(target.activeItem || 'Tutti i ticket');
          setTicketSelectedId(target.openTicketId);
          return;
        }
        // Admin-side list navigation
        if (target.contextId) setContextId(target.contextId);
        if (target.activeItem) setActiveItem(target.activeItem);
      }} data-id="element-45" />

        <main className={`flex-1 px-4 py-6 lg:px-8 lg:py-8 ${isClientMobile ? 'pb-24 md:pb-8' : ''}`} data-id="element-46">
          {profileOpen ? <Profile viewMode={viewMode} email={authUser.email} onBack={() => setProfileOpen(false)} onLogout={handleLogout} data-id="element-47" /> : notificationsOpen ? <Notifications onBack={() => setNotificationsOpen(false)} onNavigate={handleNotificationNavigate} data-id="element-48" /> : viewMode === 'operator' ? renderOperatorSection() : <>
              {clientSection === 'panoramica' && <ClientDashboard onOpenShipment={openClientShipment} onOpenTicket={openClientTicket} onOpenAllTickets={() => {
            setClientTicketSelectedId(null);
            setClientSection('ticket');
          }} data-id="element-49" />}
              {clientSection === 'spedizioni' && <ClientSpedizioni returns={clientReturns} onAddReturn={addClientReturn} initialSelectedId={clientShipmentSelectedId} data-id="element-50" />}
              {clientSection === 'resi' && <ClientReturns returns={clientReturns} onAddReturn={addClientReturn} data-id="element-51" />}
              {clientSection === 'ticket' && <ClientTickets initialSelectedId={clientTicketSelectedId} data-id="element-52" />}
            </>}
        </main>
      </div>

      {viewMode === 'client' && <BottomTabBar clientSection={clientSection} onClientSectionChange={setClientSection} data-id="element-53" />}

      {pendingNav && <div role="dialog" aria-modal="true" onClick={() => setPendingNav(null)} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-54">
          <div onClick={e => e.stopPropagation()} className="w-full max-w-sm bg-[#0F0F0F] border border-[#262626] rounded-2xl shadow-2xl overflow-hidden" data-id="element-55">
            <div className="p-5" data-id="element-56">
              <div className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-57">
                Uscire dalla nuova spedizione?
              </div>
              <p className="text-sm text-[#9A9A9A] mt-1.5 leading-relaxed" data-id="element-58">
                I dati compilati finora andranno persi. Vuoi davvero abbandonare
                la creazione della spedizione?
              </p>
            </div>
            <div className="px-5 py-3 border-t border-[#1F1F1F] flex items-center justify-end gap-2" data-id="element-59">
              <button type="button" onClick={() => setPendingNav(null)} className="px-3 py-2 rounded-lg bg-[#181818] border border-[#262626] text-sm font-medium text-[#FAFAFA] hover:border-[#333] transition" data-id="element-60">
                Continua a creare
              </button>
              <button type="button" onClick={() => {
            setContextId(pendingNav.contextId);
            if (pendingNav.activeItem) setActiveItem(pendingNav.activeItem);
            setNewShipmentOpen(false);
            setNotificationsOpen(false);
            setPendingNav(null);
          }} className="px-3 py-2 rounded-lg bg-[#EF4444] text-white text-sm font-semibold hover:bg-[#dc2f2f] transition" data-id="element-61">
                Esci senza salvare
              </button>
            </div>
          </div>
        </div>}
    </div>;
}